import {useId} from 'react';
import {useTranslation} from 'react-i18next';
import {useFetchEsimPackages} from 'hooks/esim/useFetchEsimPackages';
import {useSummary} from 'context/summary/useSummary';
import {FormProvider, useForm} from 'react-hook-form';
import {FormTypes} from './types';
import {PackageCheckbox} from './PackageCheckbox';
import {TotalToPay} from './TotalToPay';
import {useMutateEsimOrders} from 'hooks/esim/useMutateEsimOrders';
import {useReservation} from 'context/reservation/useReservation';
import {groupByDays} from './groupByDays';
import {buildCreateEsimOrderPayload} from './buildCreateEsimOrderPayload';
import cn from 'clsx';

type Props = {
  className?: string;
};
export const ChooseForm = ({className}: Props) => {
  const formId = useId();
  const {t} = useTranslation();
  const {data: summary} = useSummary();
  const {data: reservation} = useReservation();
  const {housingId, reservationId} = useReservation();
  const country = summary?.location?.country?.code;
  const {data: esimPackages, isLoading: isLoadingEsimPackages} = useFetchEsimPackages({
    country,
  });
  const {mutate: mutateEsimOrders, isPending: isMutatingEsimOrders} =
    useMutateEsimOrders();

  const groupedEsimPackages = groupByDays(esimPackages?.results);

  const methods = useForm<FormTypes>({
    shouldUnregister: true,
  });

  const submitHandler = (data: FormTypes) => {
    mutateEsimOrders(
      buildCreateEsimOrderPayload({
        data,
        reservationId: reservationId || '',
        housingId: housingId || '',
        airaloSettings: reservation?.airalo_settings,
      }),
    );
  };

  return (
    <FormProvider {...methods}>
      <form
        id={formId}
        className={cn('flex flex-col gap-6 basis-96', className)}
        onSubmit={methods.handleSubmit(submitHandler)}
      >
        <h1>{t('esim.choose_plan.title')}</h1>

        {groupedEsimPackages.map(group => (
          <fieldset key={group.days} className="flex flex-col gap-4">
            <legend className="text-sm font-semibold uppercase mb-4">
              {t('esim.choose_plan.days', {days: group.days})}
            </legend>
            {group.packages.map(esimPackage => (
              <PackageCheckbox key={esimPackage.package_id} esimPackage={esimPackage} />
            ))}
          </fieldset>
        ))}
      </form>
      <TotalToPay
        isLoading={isMutatingEsimOrders || isLoadingEsimPackages}
        esimPackages={esimPackages?.results || []}
        className="flex-1 basis-96"
        formId={formId}
      />
    </FormProvider>
  );
};
